import { match } from 'ts-pattern';
import { CustomBookingsMethodTestRun } from '~api/som';

import { Dispatch, PropsWithChildren, SetStateAction, createContext, useContext, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { FormControl } from '@ingrid/luterkort-react';

import { ContextScopeError } from '~src/errors/ContextScopeError';
import { useTranslation } from '~src/i18n/use-translation';
import { useDebounce } from '~src/lib/hooks/useDebounce';
import { useToast } from '~src/main/hooks/useToast';
import { TrackingEventEnum, useTracking } from '~src/main/hooks/useTracking';
import { generateId } from '~src/utils/generate-id';
import { toPrettyJson, tryParseJson } from '~src/utils/json';

import { useBookingRuleSaveMutation, useBookingRuleTestMutation } from '../../mutations';
import { useBookingRuleListSuspenseQuery } from '../../queries';
import { BookingRuleInputSchema, useBookingRuleForm, useBookingRuleOutputSchema, useShipmentSchema } from '../hooks';

interface ContextType {
  // Determinants whether or not the raw test shipments contains any error.
  hasInvalidRawTestShipments: boolean;
  // Set a dictionary (keyed by id) of test shipments to be evaluated against the `ruleSet`.
  setRawTestShipments: Dispatch<SetStateAction<Record<string, string>>>;
  // Get a dictionary (keyed by id) of test shipments to be evaluated against the `ruleSet`.
  rawTestShipments: Record<string, string>;
  // Set the current test shipment id, used by the editor to keep track on which shipment input to display and/or edit.
  setRawTestShipmentId: Dispatch<SetStateAction<string>>;
  // Get the current test shipment id, used by the editor to keep track on which shipment input to display and/or edit.
  rawTestShipmentId: string;
  // Get the current test shipment from `rawTestShipments` based on `rawTestShipmentId`.
  rawTestShipment: string;
  // Set which tab to show.
  setTab: Dispatch<SetStateAction<string>>;
  // Get which tab to show.
  tab: string;
  // Set the rules, evaluated by `monaco.editor.onChange` as an example.
  setRuleSet: (rule: string) => void;
  // Get the rules, consumed and used as the value for the `monaco.editor` as an example.
  ruleSet: string;
  // Test run output.
  runs: CustomBookingsMethodTestRun[];
  // The reason why running the raw test shipments against the rule set is disabled.
  disableRunReason: string | null;
  // 1. Parses the raw test shipments (rawTestShipments) via a zod schema into CreateCompositeShipmentRequest's.
  // 2. Parses the rule set via a zod schema into CustomBookingMethod's.
  // 3. Runs the test (runs the test run mutation).
  run: () => Promise<void>;
  // Determinants whether or not the test runner was mutated successfully or not.
  hasTestOutput: boolean;
  // Determinants whether or not the ruleset is currently being published.
  isPublishing: boolean;
  // Determinants whether or not the test runner is currently being mutated.
  isRunning: boolean;
}

const Context = createContext<ContextType>({
  hasInvalidRawTestShipments: undefined!,
  setRawTestShipmentId: undefined!,
  setRawTestShipments: undefined!,
  rawTestShipmentId: undefined!,
  disableRunReason: undefined!,
  rawTestShipments: undefined!,
  rawTestShipment: undefined!,
  hasTestOutput: undefined!,
  isPublishing: undefined!,
  isRunning: undefined!,
  setRuleSet: undefined!,
  setTab: undefined!,
  ruleSet: undefined!,
  runs: undefined!,
  run: undefined!,
  tab: undefined!,
});

const initialId = generateId();

export function ConfiguratorProvider({ children }: PropsWithChildren) {
  const [rawTestShipments, setRawTestShipments] = useState<Record<string, string>>({ [initialId]: toPrettyJson({}) });
  const [rawTestShipmentId, setRawTestShipmentId] = useState(initialId);
  const [tab, setTab] = useState('input');

  const { data = [] } = useBookingRuleListSuspenseQuery();
  const { t } = useTranslation();
  const { toast } = useToast();

  const form = useBookingRuleForm({ defaultValue: toPrettyJson(data) });
  const publishMutation = useBookingRuleSaveMutation();
  const testMutation = useBookingRuleTestMutation();
  const ruleSchema = useBookingRuleOutputSchema();
  const shipmentSchema = useShipmentSchema();
  const ruleSet = form.watch('json');
  const tracking = useTracking();

  const rawTestShipment = useMemo(() => rawTestShipments[rawTestShipmentId], [rawTestShipmentId, rawTestShipments]);
  const runs = useMemo(() => testMutation.data?.data.test_run_results ?? [], [testMutation]);
  const isPublishing = useMemo(() => publishMutation.isPending, [publishMutation]);
  const hasTestOutput = useMemo(() => testMutation.isSuccess, [testMutation]);
  const isRunning = useMemo(() => testMutation.isPending, [testMutation]);

  const hasInvalidRawTestShipments = useDebounce(
    useMemo(() => {
      return Object.values(rawTestShipments).some(s => !shipmentSchema.safeParse(tryParseJson(s, false)).success);
    }, [rawTestShipments, shipmentSchema]),
    300
  );

  const hasValidRuleSet = useDebounce(
    useMemo(() => ruleSchema.safeParse(tryParseJson(ruleSet, false)).success, [ruleSet, ruleSchema]),
    300
  );

  const disableRunReason = match({ hasInvalidRawTestShipments, hasValidRuleSet })
    .with({ hasInvalidRawTestShipments: true }, () => t('bookingRules.edit.input.errorTooltip'))
    .with({ hasValidRuleSet: false }, () => t('bookingRules.edit.rule.errorTooltip'))
    .otherwise(() => null);

  async function handlePublish(values: BookingRuleInputSchema) {
    try {
      const parsed: unknown = JSON.parse(values.json);
      const data = ruleSchema.parse(parsed);

      const promise = publishMutation.mutateAsync(data);

      await toast.promise(promise, {
        success: { title: t('bookingRules.edit.publish.success') },
        error: { title: t('bookingRules.edit.publish.failed') },
        loading: { title: t('bookingRules.edit.publish.loading') },
      });

      tracking.capture(TrackingEventEnum.ConfiguratorPublishCompleted);
    } catch (e) {
      tracking.capture(TrackingEventEnum.ConfiguratorPublishFailed);
      throw e;
    }
  }

  async function run() {
    const rawTestShipmentValues = Object.values(rawTestShipments);

    try {
      const parsedShipments = rawTestShipmentValues.map(s => shipmentSchema.parse(JSON.parse(s)));
      const parsedRules = ruleSchema.parse(JSON.parse(ruleSet));

      await testMutation.mutateAsync({
        custom_booking_methods: parsedRules,
        shipments: parsedShipments,
      });

      if (tab === 'input') {
        setTab('output');
      }

      tracking.capture(TrackingEventEnum.ConfiguratorRunTestCompleted, {
        numberOfShipments: rawTestShipmentValues.length,
      });
    } catch (e) {
      tracking.capture(TrackingEventEnum.ConfiguratorRunTestFailed, {
        numberOfShipments: rawTestShipmentValues.length,
      });
      throw e;
    }
  }

  function setRuleSet(input: string) {
    form.setValue('json', input);
  }

  return (
    <Context.Provider
      value={{
        hasInvalidRawTestShipments,
        ruleSet,
        isPublishing,
        isRunning,
        hasTestOutput,
        tab,
        rawTestShipments,
        rawTestShipmentId,
        rawTestShipment,
        disableRunReason,
        runs,
        setRuleSet,
        run,
        setTab,
        setRawTestShipments,
        setRawTestShipmentId,
      }}
    >
      <FormControl.Root>
        <FormControl.Form onSubmit={form.handleSubmit(handlePublish)} />
        <FormProvider {...form}>{children}</FormProvider>
      </FormControl.Root>
    </Context.Provider>
  );
}

export function useConfiguratorContext() {
  const ctx = useContext(Context);

  if (!ctx) {
    throw new ContextScopeError('useConfiguratorContext', 'ConfiguratorProvider');
  }

  return ctx;
}
